import {
  WISHLIST_EVENT_NAME,
  WISHLIST_STORAGE_KEY,
  buildContactUrl,
  buildVariantUrl,
  resolveWishlistCardState,
} from './gvode-wishlist-core.js';
import { getWishlistStore } from './gvode-wishlist-store.js';

export async function loadWishlistProducts(items, productsRoot, request = fetch) {
  const handles = [...new Set(items.map((item) => item.handle).filter(Boolean))];
  const results = await Promise.allSettled(
    handles.map(async (handle) => {
      const response = await request(`${productsRoot}${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      return [handle, await response.json()];
    })
  );

  return new Map(results.filter((result) => result.status === 'fulfilled').map((result) => result.value));
}

export class GvodeWishlistPage extends HTMLElement {
  async connectedCallback() {
    this.store = getWishlistStore();
    this.render();
    document.addEventListener(WISHLIST_EVENT_NAME, this.onChange);
    window.addEventListener('storage', this.onStorage);
    await this.hydrate();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT_NAME, this.onChange);
    window.removeEventListener('storage', this.onStorage);
    this.hydrationToken = null;
  }

  onChange = () => {
    this.render();
    void this.hydrate();
  };

  onStorage = (event) => {
    if (event.key === WISHLIST_STORAGE_KEY || event.key === null) {
      this.render();
      void this.hydrate();
    }
  };

  render() {
    const items = this.store.getItems();
    const list = this.querySelector('[data-ref="list"]');
    const empty = this.querySelector('[data-ref="empty"]');
    if (!list || !empty) return;

    list.replaceChildren(...items.map((item, index) => this.card(item, index)));
    empty.hidden = items.length !== 0;
  }

  card(item, index) {
    const template = this.querySelector('[data-ref="cardTemplate"]');
    const node = template.content.firstElementChild.cloneNode(true);
    node.dataset.variantId = item.variantId;
    node.querySelector('[data-field="title"]').textContent = item.productTitle || 'Saved product';
    node.querySelector('[data-field="variant"]').textContent = `${item.variantTitle || 'Saved option'} · SKU ${
      item.sku || 'Not provided'
    }`;

    const image = node.querySelector('[data-field="image"]');
    if (item.imageUrl) {
      image.src = item.imageUrl;
      image.alt = item.productTitle || 'Saved product';
    } else {
      image.hidden = true;
    }

    node.querySelector('[data-action="remove"]').addEventListener('click', () => this.remove(item, index));
    this.applyState(node, item, { status: 'unknown', variant: null });
    return node;
  }

  remove(item, index) {
    const result = this.store.remove(item.variantId);
    const region = this.querySelector('[data-ref="liveRegion"]');
    if (region) {
      region.textContent = result.ok
        ? `${item.productTitle || 'Item'} removed from your wishlist.`
        : 'Wishlist could not be updated in this browser.';
    }
    if (!result.ok) return;

    const remaining = this.querySelectorAll('.gvode-wishlist-card [data-action="remove"]');
    const next = remaining[Math.min(index, remaining.length - 1)];
    (next ?? this.querySelector('h1'))?.focus();
  }

  async hydrate() {
    const items = this.store.getItems();
    const token = Symbol('wishlist-hydration');
    this.hydrationToken = token;
    const list = this.querySelector('[data-ref="list"]');
    list?.setAttribute('aria-busy', String(items.length > 0));

    const products = await loadWishlistProducts(items, this.dataset.productsRoot);
    if (this.hydrationToken !== token) return;

    for (const item of items) {
      const node = [...this.querySelectorAll('.gvode-wishlist-card')].find(
        (candidate) => candidate.dataset.variantId === item.variantId
      );
      if (node) this.applyState(node, item, resolveWishlistCardState(products.get(item.handle) ?? null, item));
    }

    list?.setAttribute('aria-busy', 'false');
  }

  applyState(node, item, state) {
    const status = node.querySelector('[data-field="status"]');
    const primary = node.querySelector('[data-action="primary"]');
    const contact = node.querySelector('[data-action="contact"]');
    if (!status || !primary || !contact) return;

    status.dataset.status = state.status;
    contact.hidden = true;

    if (state.status === 'sold_out') {
      status.textContent = 'Sold out';
      primary.textContent = 'Contact Us';
      primary.href = buildContactUrl(item);
    } else if (state.status === 'in_stock') {
      status.textContent = 'In stock';
      primary.textContent = 'View product';
      primary.href = buildVariantUrl(item.productUrl, item.variantId);
    } else if (state.status === 'option_unavailable') {
      status.textContent = 'Option unavailable';
      primary.textContent = 'View product';
      primary.href = item.productUrl;
    } else {
      status.textContent = 'Status unavailable';
      primary.textContent = 'View product';
      primary.href = item.productUrl;
      contact.href = buildContactUrl(item);
      contact.hidden = false;
    }
  }
}

if (!customElements.get('gvode-wishlist-page')) {
  customElements.define('gvode-wishlist-page', GvodeWishlistPage);
}
